import { matchCall, matchLastQuotedArg } from "../domUtils";
import type { PrototypeState } from "../controllerState";
import type { Root } from "../prototypeTypes";
import { closeModalStackForRoute, setActiveTab, showArticle, showView } from "../navigationController";
import { showTopicDetail } from "../topicDetailView";
import { nextArticleChapter, previousArticleChapter } from "../articleController";
import { refreshBrowseGrid, setPendingBrowseFilter, runBrowseSearch, clearBrowseSearch, setBrowseSort } from "../browseController";
import { recordRuntimeError } from "../prototypeRuntimeSupport";
import { handleApiError } from "../authGuards";
import { celebrate } from "../celebration";
import { showToast } from "../toastController";
import { activateBrowseFilter, filterBrowse } from "./browseFilterActions";
import { captureCommunityReturnState } from '../communityMountState';
import { browsePaginationScopeKey, markBrowseTopicDeparture } from '../browsePaginationSessionStore';
import { captureHomeReturnState } from '../homeReturnState';
import type { BrowseSort } from '../browseData';
import { requestAudioPlayback } from '../actions/mediaActions';
import { paintMiniPlayerFavorite, previewNowPlayingChapter, showPlayer } from '../playerUiController';
import { beginAudioHandoffCommandIntent, isAudioHandoffIntentCurrent } from '../audioHandoffController';
import { mountedFor, pauseMediaPlayerFromUser, playMediaPlayer, presentOptimisticAudioPlayback, rollbackOptimisticAudioPlayback } from '../mediaPlayer';
import { recordChapterProgress } from '../chapterProgressTracker';
import { reconcileHomeFavorites, reconcileHomeRecent, removeHomeFavoriteCard } from '../homeRecentHydrator';
import { patchActiveBrowseTopicViewerState } from '../browsePaginationSessionStore';
import { patchBrowseViewTopicViewerState } from '../topicBrowseViewCache';
import { startAudioPlaybackSingleFlight, toggleAudioPlaybackSingleFlight } from '../audioPlaybackSingleFlight';
import { bumpTopicFavoriteVersion, bumpTopicVoteVersion } from '../topicFavoriteVersion';
import { closeChapterDirectory, openChapterDirectory } from '../chapterDirectoryController';
import { getTopic } from '../topicStore';
import { isLogoutPending } from './authRouteActions';

const topicVoteRequests = new WeakMap<object, Set<string>>();
const topicFavoriteRequests = new WeakMap<object, Set<string>>();
let homeRecentValidationSequence = 0;
// The vote/favorite locks are in-flight guards, not cooldowns: cap how long
// they may hold the controls so a hung API cannot wedge the UI.
const TOPIC_ACTION_PENDING_CAP_MS = 1_000;
const topicActionPendingTimers = new WeakMap<object, Map<string, ReturnType<typeof setTimeout>>>();

export async function routeLearningActions(normalized: string, root: Root, source: HTMLElement, state: PrototypeState) {
	const topicId = matchCall(normalized, "showTopicDetail") ?? "";
	const githubTopicId = matchCall(normalized, 'openGhRepo') ?? '';
	if (normalized.includes('scrollHomeToTop')) {
		const reduceMotion = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
		if (!reduceMotion) source.dataset.homeTopScrollResetting = 'true';
		window.scrollTo({ top: 0, behavior: reduceMotion ? 'instant' as ScrollBehavior : 'smooth' });
		if (reduceMotion) delete source.dataset.homeTopScrollResetting;
		delete source.dataset.homeBackToTop;
		source.dataset.protoClick = "showView('home');setActiveTab('home')";
		source.removeAttribute('aria-label');
		source.querySelector<HTMLElement>('.tab-home-label')!.textContent = 'Home';
		source.querySelector<SVGElement>('.tab-home-icon')!.removeAttribute('hidden');
		source.querySelector<SVGElement>('.tab-rocket-icon')!.setAttribute('hidden', '');
		return;
	}
	if (normalized.includes('openHistoryTopic')) {
		openHistoryTopic(source.dataset.historyTopicSlug ?? '', root, state);
		return;
	}
	if (normalized.includes('resumeHistoryChapter')) {
		await resumeHistoryChapter(root, source, state);
		return;
	}
	if (normalized.includes('playHomeRecentChapter')) {
		await openHomeRecentChapter(root, source, state, true);
		return;
	}
	if (normalized.includes('openHomeRecentChapter')) {
		await openHomeRecentChapter(root, source, state);
		return;
	}
  if (normalized.includes("resumeListening")) {
    await resumeLearning(root, state);
  }
  if (normalized.includes("showTopicDetail")) {
		const communityReturnState = captureCommunityReturnState(root, source);
		const homeReturnState = captureHomeReturnState(root, source, topicId);
		const currentState = window.history.state && typeof window.history.state === 'object' ? window.history.state : {};
		const { communityReturnState: staleCommunityReturnState, homeReturnState: staleHomeReturnState, ...stateWithoutReturnState } = currentState;
		const holder = root as unknown as { __browseFilter?: string; __browseSort?: BrowseSort };
		const browseGrid = root.querySelector<HTMLElement>('#view-home-browse.active #browseGrid');
		const fromActiveBrowse = Boolean(browseGrid && source.closest('#view-home-browse.active #browseGrid') === browseGrid);
		const search = root.querySelector<HTMLInputElement>('#browseSearchInput')?.value.trim() ?? '';
		const initialFavorite = readTopicFavoriteFromSource(source, topicId);
		const browseReturnSession = fromActiveBrowse
			? markBrowseTopicDeparture(root, browsePaginationScopeKey(state.runtime, holder.__browseFilter ?? 'all', search, holder.__browseSort ?? 'trending'), window.scrollY)
			: null;
		if (communityReturnState || homeReturnState || staleCommunityReturnState !== undefined || staleHomeReturnState !== undefined || browseReturnSession) {
			window.history.replaceState({
				...(communityReturnState ? { ...stateWithoutReturnState, communityReturnState } : stateWithoutReturnState),
				...(homeReturnState ? { homeReturnState } : {}),
				...(browseReturnSession ? { browseReturnSession } : {}),
			}, '');
		}
    state.runtime.activeCourseId = topicId;
    state.runtime.activeChapterIndex = 1;
    showTopicDetail(topicId, root, state.effectiveAccessPlan, undefined, {
			...(fromActiveBrowse ? { origin: 'browse-card' } : {}),
			...(initialFavorite === undefined ? {} : { initialViewerState: { isFavorite: initialFavorite } }),
		});
    setActiveTab("", root);
    void recordTopicOpened(root, state, topicId);
  }
	if (normalized.includes('openGhRepo') && githubTopicId) {
		state.runtime.activeCourseId = githubTopicId;
		state.runtime.activeChapterIndex = 1;
		showTopicDetail(githubTopicId, root, state.effectiveAccessPlan);
		setActiveTab('', root);
		void recordTopicOpened(root, state, githubTopicId);
	}
	if (normalized.includes("showArticle")) {
		if (root.querySelector('#chapterDirectoryModal.active')) await closeModalStackForRoute(root, ['chapterDirectoryModal']);
    const chapterIndex = Number(matchCall(normalized, "showArticle") ?? 1);
    state.runtime.activeChapterIndex = chapterIndex;
		showArticle(chapterIndex, root, state.effectiveAccessPlan, { recordProgress: state.isLoggedIn });
  }
	if (normalized.includes('playTopicChapter')) {
		const chapterIndex = Number(matchCall(normalized, 'playTopicChapter') ?? 0);
		if (Number.isInteger(chapterIndex) && chapterIndex > 0 && state.runtime.activeCourseId) {
			state.runtime.activeChapterIndex = chapterIndex;
			const row = source.closest<HTMLElement>('.article-item');
			const title = row?.querySelector<HTMLElement>('.article-title')?.textContent?.trim() || `Chapter ${chapterIndex}`;
			(root as unknown as { __activeChapter?: { index: number; title: string; topicTitle: string; topicSlug: string } }).__activeChapter = {
				index: chapterIndex,
				title,
				topicTitle: getTopic(state.runtime.activeCourseId)?.title || state.runtime.activeCourseId,
				topicSlug: state.runtime.activeCourseId,
			};
		}
	}
	if (normalized.includes('openChapterDirectory')) {
		await openChapterDirectory(root, source);
		return;
	}
	if (normalized.includes('closeChapterDirectory')) {
		closeChapterDirectory(root);
		return;
	}
  if (normalized.includes("toggleVote")) {
    const voteTopicId = matchLastQuotedArg(normalized, "toggleVote") ?? source.closest<HTMLElement>("[data-id]")?.dataset.id ?? "";
    if (!voteTopicId) return;
    // Parse the vote value: toggleVote(this,'slug',1) or toggleVote(this,'slug',-1)
    const voteArgMatch = normalized.match(/toggleVote\([^)]*,\s*(-?\d+)\s*\)/);
    const requestedVote = voteArgMatch ? Number(voteArgMatch[1]) : 0;
    if (requestedVote !== 1 && requestedVote !== -1) return;
		if (isTopicActionPending(topicVoteRequests, root, voteTopicId)) return;
    const currentVoteValue = readTopicVoteState(root, voteTopicId);
    const isCurrentlyActive = currentVoteValue === requestedVote;
    // Cancel: clicking the already-active button sets vote to 0
		const newVoteValue = isCurrentlyActive ? 0 : requestedVote;
		markTopicVoteWrite(root, voteTopicId, source);
		setTopicActionPending(topicVoteRequests, root, voteTopicId, true);
		syncTopicVoteState(root, voteTopicId, newVoteValue);
		syncBrowseTopicViewerState(root, voteTopicId, { voteValue: newVoteValue as -1 | 0 | 1 });
    if (newVoteValue !== 0) {
			source.classList.add('activating');
			setTimeout(() => source.classList.remove('activating'), 600);
    }
    void state.runtime.api.updateTopicVote({ topicId: voteTopicId, voteValue: newVoteValue as 1 | -1 | 0 })
		.then((response) => {
			const persisted = (response as { voteValue?: unknown }).voteValue;
			if (persisted !== -1 && persisted !== 0 && persisted !== 1) throw new Error('topic_vote_response_invalid');
			syncTopicVoteState(root, voteTopicId, persisted);
			markTopicVoteWrite(root, voteTopicId, source);
			syncBrowseTopicViewerState(root, voteTopicId, { voteValue: persisted });
		})
		.catch((error) => {
			syncTopicVoteState(root, voteTopicId, currentVoteValue);
			markTopicVoteWrite(root, voteTopicId, source);
			syncBrowseTopicViewerState(root, voteTopicId, { voteValue: currentVoteValue });
        handleApiError(state.runtime, state.effectiveAccessPlan, state.isLoggedIn, error, { root, intent: "vote" });
		})
		.finally(() => setTopicActionPending(topicVoteRequests, root, voteTopicId, false));
  }
  if (normalized.includes("toggleStar")) {
    const starTopicId = matchLastQuotedArg(normalized, "toggleStar") ?? source.dataset.topicId ?? source.closest<HTMLElement>("[data-id]")?.dataset.id ?? "";
    if (!starTopicId) return;
		if (isTopicActionPending(topicFavoriteRequests, root, starTopicId)) return;
    // Toggle favorite state. Both card footer buttons (.fav-btn) and
    // the detail page button (#topicFavBtn) use the "active" class.
    // Legacy .star-btn elements (if any remain) use "starred".
		// The rendered action state is shared by Browse, Saved and Topic detail.
		// A retained card can outlive an earlier optimistic class transition, so a
		// decorative `starred` class is not a safe source of truth here. Read the
		// normalized, accessible state for this Topic before calculating the write.
    const wasActive = readTopicFavoriteState(root, starTopicId, source);
		markTopicFavoriteWrite(root, starTopicId, source);
		setTopicActionPending(topicFavoriteRequests, root, starTopicId, true, { keepMiniFavoriteEnabled: true });
    source.classList.toggle("active", !wasActive);
    source.classList.toggle("starred", !wasActive);
		source.setAttribute('aria-pressed', String(!wasActive));
		const isFavorited = !wasActive;
		syncFavoriteCardState(root, starTopicId, isFavorited);
		syncBrowseTopicViewerState(root, starTopicId, { isFavorite: isFavorited });
		if (source.id === 'miniFavoriteBtn') {
			// The dock uses its final favorite color immediately. `favoritePending`
			// only fences stale list reads; it is not a visual loading state.
			source.dataset.favoritePending = 'true';
			paintMiniPlayerFavorite(source, isFavorited);
		}
    source.closest<HTMLElement>(".topic-card")?.toggleAttribute("data-favorited", isFavorited);
    if (isFavorited) {
      // Lightweight confetti burst: 18 particles, 1.2s - avoids the jank
      // caused by the full 46-particle / 3s celebration on every save click.
      celebrate("favorite", undefined, { durationMs: 1200, count: 18 });
      // Animation: bounce the saved-topic Bookmark
      source.classList.add("activating");
      setTimeout(() => source.classList.remove("activating"), 500);
    }
    void state.runtime.api.updateFavorite({ topicId: starTopicId, favorited: isFavorited })
		.then((response) => {
				const persisted = (response as { favorited?: unknown }).favorited;
				if (typeof persisted !== 'boolean') throw new Error('favorite_response_invalid');
				syncFavoriteCardState(root, starTopicId, persisted);
				// A player/list read can begin after the optimistic state is painted but
				// before this response settles. Advance the same Topic fence again so
				// that old response cannot replace the committed server result.
				markTopicFavoriteWrite(root, starTopicId, source);
				syncBrowseTopicViewerState(root, starTopicId, { isFavorite: persisted });
				if (!persisted) removeHomeFavoriteCard(root, starTopicId);
				// The write is authoritative. Read Saved back silently only after it
				// succeeds, so a failed optimistic write never repaints this rail.
				void reconcileHomeFavorites(root, state.runtime.api);
      })
      .catch((error) => {
        // Rollback optimistic UI on error
        source.classList.toggle("active", wasActive);
        source.classList.toggle("starred", wasActive);
			source.setAttribute('aria-pressed', String(wasActive));
			syncFavoriteCardState(root, starTopicId, wasActive);
			markTopicFavoriteWrite(root, starTopicId, source);
			syncBrowseTopicViewerState(root, starTopicId, { isFavorite: wasActive });
        handleApiError(state.runtime, state.effectiveAccessPlan, state.isLoggedIn, error, { root, intent: "favorite" });
			})
			.finally(() => {
				if (source.id === 'miniFavoriteBtn') delete source.dataset.favoritePending;
				setTopicActionPending(topicFavoriteRequests, root, starTopicId, false);
			});
  }
  if (normalized.includes("activateBrowseFilter")) {
    const filter = matchCall(normalized, "activateBrowseFilter") ?? "all";
    await activateBrowseFilter(root, state, filter);
  }
  if (normalized.includes("filterBrowse")) {
    // Keep the existing delegated action compatible with hydrated category
    // controls. `filterBrowse` repeats this validation at its public boundary
    // for direct callers; this early extraction preserves the router contract.
    const sourceCategory = source.dataset.browseCategory;
    const filter = sourceCategory && /^[a-z0-9]+(?:-[a-z0-9]+)*$/u.test(sourceCategory)
      ? sourceCategory
      : matchCall(normalized, "filterBrowse") ?? "all";
    await filterBrowse(root, source, state, filter);
  }
	if (normalized.includes('setBrowseSort')) {
		const requestedSort = matchCall(normalized, 'setBrowseSort');
		const sort = requestedSort === 'newest' ? 'newest' : 'trending';
		setBrowseSort(root, sort);
	}
  if (normalized.includes("refreshBrowse")) {
    // Retry: re-fetch the currently selected capsule rather than
    // resetting to the default. Read it from the holder state so an
    // error on "favorites" retries "favorites".
    const holder = root as unknown as { __browseFilter?: string; __browseRuntime?: typeof state.runtime };
    const currentFilter = holder.__browseFilter ?? "recent";
    const rt = holder.__browseRuntime ?? state.runtime;
    setPendingBrowseFilter(currentFilter);
    await refreshBrowseGrid(root, rt);
  }
  if (normalized.includes("runBrowseSearch")) {
    runBrowseSearch(root);
  }
  if (normalized.includes("clearBrowseSearch")) {
    clearBrowseSearch(root);
  }
  if (normalized.includes("navigateToBrowse")) {
    const filter = matchCall(normalized, "navigateToBrowse") ?? "all";
		const sortMatch = normalized.match(/\bnavigateToBrowse\(\s*['"][^'"]+['"]\s*,\s*['"](trending|newest)['"]/);
    await activateBrowseFilter(root, state, filter, sortMatch?.[1] as BrowseSort | undefined);
  }
  if (normalized.includes("nextArticleChapter")) {
		await nextArticleChapter(root, state.effectiveAccessPlan, state.isLoggedIn);
  }
  if (normalized.includes("previousArticleChapter")) {
    previousArticleChapter(root, state.effectiveAccessPlan);
  }
}

function openHistoryTopic(topicSlug: string, root: Root, state: PrototypeState): void {
	if (!topicSlug) return;
	state.runtime.activeCourseId = topicSlug;
	state.runtime.activeChapterIndex = 1;
	showTopicDetail(topicSlug, root, state.effectiveAccessPlan);
	setActiveTab('', root);
}

async function resumeHistoryChapter(root: Root, source: HTMLElement, state: PrototypeState): Promise<void> {
	const topicSlug = source.dataset.historyTopicSlug ?? '';
	const chapterId = source.dataset.historyChapterId ?? '';
	const chapterIndex = Number(source.dataset.historyChapterIndex);
	const progressPercent = Number(source.dataset.historyProgressPercent);
	const fallback = (message: string) => {
		openHistoryTopic(topicSlug, root, state);
		showToast({ type: 'warning', message, durationMs: 5000 });
	};

	if (!topicSlug || !chapterId || !Number.isInteger(chapterIndex) || chapterIndex < 1) {
		fallback('This chapter is no longer available. Opening the topic instead.');
		return;
	}

	try {
		const response = await state.runtime.api.listTopicChapters(topicSlug);
		const chapter = response.chapters?.find((candidate) => candidate.index === chapterIndex);
		if (!chapter || chapter.id !== chapterId) {
			fallback('This chapter is no longer available. Opening the topic instead.');
			return;
		}
		state.runtime.activeCourseId = topicSlug;
		state.runtime.activeChapterIndex = chapterIndex;
		state.runtime.chapterIdByIndex[topicSlug] = {
			...(state.runtime.chapterIdByIndex[topicSlug] ?? {}),
			[chapterIndex]: chapterId,
		};
		state.runtime.resumeProgressByChapter = {
			...(state.runtime.resumeProgressByChapter ?? {}),
			[`${topicSlug}:${chapterIndex}`]: Number.isFinite(progressPercent) ? Math.max(0, Math.min(100, progressPercent)) : 0,
		};
		showArticle(chapterIndex, root, state.effectiveAccessPlan);
	} catch {
		fallback("We couldn't resume this chapter. Opening the topic instead.");
	}
}

async function openHomeRecentChapter(root: Root, source: HTMLElement, state: PrototypeState, playAudio = false): Promise<void> {
	const card = source.closest<HTMLElement>('.recent-card') ?? source;
	const topicSlug = card.dataset.homeRecentTopicSlug ?? '';
	const chapterId = card.dataset.homeRecentChapterId ?? '';
	const chapterIndex = Number(card.dataset.homeRecentChapterIndex);
	const chapterTitle = card.dataset.homeRecentChapterTitle ?? '';
	const progressPercent = Number(card.dataset.homeRecentProgressPercent);
	const resumeSeconds = Number(card.dataset.homeRecentPositionSeconds);
	const persistHomeReturnState = () => {
		const homeReturnState = captureHomeReturnState(root, card, topicSlug);
		if (!homeReturnState) return;
		const currentState = window.history.state && typeof window.history.state === 'object' ? window.history.state as Record<string, unknown> : {};
		window.history.replaceState({ ...currentState, homeReturnState }, '');
	};
	const fallback = (message: string) => {
		persistHomeReturnState();
		openHistoryTopic(topicSlug, root, state);
		showToast({ type: 'warning', message, durationMs: 5000 });
	};

	if (!topicSlug || !chapterId || chapterId.length > 512 || !Number.isInteger(chapterIndex) || chapterIndex < 1) {
		fallback('This chapter is no longer available. Opening the topic instead.');
		return;
	}
	persistHomeReturnState();
	if (playAudio) {
		// A finished chapter never resumes. From this non-switch entry the user
		// lands on the NEXT chapter at 0:00; the final chapter wraps to Chapter 1
		// (full topic replay). Unfinished chapters keep precise resume and still
		// skip the chapter-list request: the playback-token endpoint remains the
		// authority for stale/deleted chapters and access, and an extra await
		// here risked losing the click's transient activation.
		let targetIndex = chapterIndex;
		let targetChapterId = chapterId;
		let targetTitle = chapterTitle;
		const restartFromZero = card.dataset.homeRecentCompleted === '1';
		if (restartFromZero) {
			try {
				const response = await state.runtime.api.listTopicChapters(topicSlug);
				const chapters = (Array.isArray(response.chapters) ? response.chapters : [])
					.slice()
					.sort((a, b) => a.index - b.index);
				if (chapters.length > 0) {
					const next = chapters.find((candidate) => candidate.index > chapterIndex)
						?? chapters.find((candidate) => candidate.index === 1)
						?? chapters[0];
					if (next && next.index !== chapterIndex) {
						targetIndex = next.index;
						targetChapterId = next.id;
						targetTitle = next.title;
					}
				}
			} catch {
				// Chapter list unavailable: replay the card's own chapter from 0:00.
			}
		}
		state.runtime.activeCourseId = topicSlug;
		state.runtime.activeChapterIndex = targetIndex;
		state.runtime.chapterIdByIndex[topicSlug] = {
			...(state.runtime.chapterIdByIndex[topicSlug] ?? {}),
			[targetIndex]: targetChapterId,
		};
		state.runtime.resumeProgressByChapter = {
			...(state.runtime.resumeProgressByChapter ?? {}),
			[`${topicSlug}:${targetIndex}`]: restartFromZero ? 0 : Number.isFinite(progressPercent) ? Math.max(0, Math.min(100, progressPercent)) : 0,
		};
		if (restartFromZero || (Number.isFinite(resumeSeconds) && resumeSeconds >= 0)) {
			state.runtime.mediaResumeSecondsByChapter = {
				...(state.runtime.mediaResumeSecondsByChapter ?? {}),
				[`${topicSlug}:${targetIndex}`]: restartFromZero ? 0 : resumeSeconds,
			};
		}
		(root as unknown as { __activeChapter?: { index: number; title: string; topicTitle: string; topicSlug: string } }).__activeChapter = {
			index: targetIndex,
			title: targetTitle || `Chapter ${targetIndex}`,
			topicTitle: card.dataset.homeRecentTopicTitle || getTopic(topicSlug)?.title || topicSlug,
			topicSlug,
		};
		const mountedAudio = mountedFor(root, 'audio');
		// A finished card replays from 0:00, so a paused stale element is
		// re-mounted; a currently playing element still just toggles pause.
		if (mountedAudio?.topicId === topicSlug && mountedAudio.chapterIndex === targetIndex && !mountedAudio.element.ended
			&& (!restartFromZero || !mountedAudio.element.paused)) {
			if (!mountedAudio.element.paused) {
				pauseMediaPlayerFromUser(root, 'audio');
				return;
			}
			// Start the existing element before any await so an already-prepared
			// Recent item always consumes the original click activation.
			showPlayer(root);
			await playMediaPlayer(root, 'audio');
			return;
		}
		// A successful `playing` event consumes the handoff intent before the
		// token/play promise settles. Reveal the mini player first; checking that
		// intent afterwards made a real playing audio element remain invisible.
		const target = { topicId: topicSlug, chapterIndex: targetIndex };
		const desiredFromPending = toggleAudioPlaybackSingleFlight(root, target);
		if (desiredFromPending !== null) {
			presentOptimisticAudioPlayback(root, { ...target, chapterTitle: targetTitle, paused: !desiredFromPending });
			previewNowPlayingChapter(root, {
				index: targetIndex,
				title: targetTitle || `Chapter ${targetIndex}`,
				topicTitle: (root as unknown as { __activeChapter?: { topicTitle?: string } }).__activeChapter?.topicTitle
					|| card.dataset.homeRecentTopicTitle || getTopic(topicSlug)?.title || topicSlug,
				topicSlug,
				topicSubtitle: '',
			});
			return;
		}
		const commandIntent = beginAudioHandoffCommandIntent(root);
		presentOptimisticAudioPlayback(root, { ...target, chapterTitle: targetTitle, paused: false });
		previewNowPlayingChapter(root, {
			index: targetIndex,
			title: targetTitle || `Chapter ${targetIndex}`,
			topicTitle: (root as unknown as { __activeChapter?: { topicTitle?: string } }).__activeChapter?.topicTitle
				|| card.dataset.homeRecentTopicTitle || getTopic(topicSlug)?.title || topicSlug,
			topicSlug,
			topicSubtitle: '',
		});
		const flight = startAudioPlaybackSingleFlight(root, target, true, (desiredPlaying) =>
			requestAudioPlayback(state.runtime, state.effectiveAccessPlan, root, recordRuntimeError, { desiredPlaying }, commandIntent),
		);
		const result = await flight.promise;
		if (result !== 'playing' && flight.isDesiredPlaying() && isAudioHandoffIntentCurrent(root, commandIntent)) rollbackOptimisticAudioPlayback(root, target);
		return;
	}

	const viewerScope = `${state.runtime.userId}|${state.runtime.sessionHash}`;
	const validationEntryId = `home-recent-${++homeRecentValidationSequence}`;
	const articlePath = `/topics/${encodeURIComponent(topicSlug)}/chapters/${chapterIndex}`;
	state.runtime.activeCourseId = topicSlug;
	state.runtime.activeChapterIndex = chapterIndex;
	state.runtime.chapterIdByIndex[topicSlug] = {
		...(state.runtime.chapterIdByIndex[topicSlug] ?? {}),
		[chapterIndex]: chapterId,
	};
	state.runtime.resumeProgressByChapter = {
		...(state.runtime.resumeProgressByChapter ?? {}),
		[`${topicSlug}:${chapterIndex}`]: Number.isFinite(progressPercent) ? Math.max(0, Math.min(100, progressPercent)) : 0,
	};
	// Keep the route transition on the click path. The Article shell is safe to
	// show from the card's opaque chapter id; the body remains deferred until
	// the authoritative topic list confirms that id still belongs to this index.
	showArticle(chapterIndex, root, state.effectiveAccessPlan, {
		deferChapterLoad: true,
		historyState: { homeRecentValidationEntryId: validationEntryId },
	});
	const ownsDeferredRecentNavigation = () => state.isLoggedIn
		&& `${state.runtime.userId}|${state.runtime.sessionHash}` === viewerScope
		&& state.runtime.activeCourseId === topicSlug
		&& state.runtime.activeChapterIndex === chapterIndex
		&& root.querySelector('#view-article.active') !== null
		&& window.location.pathname.replace(/\/+$/u, '') === articlePath
		&& window.history.state?.homeRecentValidationEntryId === validationEntryId;
	const fallbackDeferredNavigation = (message: string) => {
		// The optimistic Article entry already occupies the history slot. Replace
		// that exact entry with Topic so Back reaches the original Home entry,
		// whose return snapshot was captured before the click.
		const currentState = window.history.state && typeof window.history.state === 'object' ? window.history.state as Record<string, unknown> : {};
		const { homeRecentValidationEntryId: _validationEntryId, ...topicState } = currentState;
		window.history.replaceState(topicState, '', `/topics/${encodeURIComponent(topicSlug)}`);
		openHistoryTopic(topicSlug, root, state);
		showToast({ type: 'warning', message, durationMs: 5000 });
	};
	try {
		const response = await state.runtime.api.listTopicChapters(topicSlug);
		if (!ownsDeferredRecentNavigation()) return;
		const chapter = response.chapters?.find((candidate) => candidate.index === chapterIndex);
		if (!chapter || chapter.id !== chapterId) {
			fallbackDeferredNavigation('This chapter is no longer available. Opening the topic instead.');
			return;
		}
		showArticle(chapterIndex, root, state.effectiveAccessPlan, { historyMode: 'none' });
	} catch {
		if (!ownsDeferredRecentNavigation()) return;
		fallbackDeferredNavigation("We couldn't resume this chapter. Opening the topic instead.");
	}
}

async function recordTopicOpened(root: Root, state: PrototypeState, topicId: string) {
  if (!state.isLoggedIn || !topicId) return;
	const viewerScope = `${state.runtime.userId}|${state.runtime.sessionHash}`;
  try {
    const response = await state.runtime.api.listTopicChapters(topicId);
		// `logout()` revokes the cookie asynchronously. A topic lookup that was
		// already pending must not create a progress POST in that revocation
		// window, and a later account/session must never inherit this intent.
		if (!state.isLoggedIn || isLogoutPending(root) || `${state.runtime.userId}|${state.runtime.sessionHash}` !== viewerScope) return;
    const first = response.chapters?.[0];
    if (!first?.id) return;
    state.runtime.chapterIdByIndex[topicId] = {
      ...(state.runtime.chapterIdByIndex[topicId] ?? {}),
      [first.index]: first.id,
    };
	if (state.runtime.activeCourseId !== topicId || state.runtime.activeChapterIndex !== 1) return;
	await recordChapterProgress(state.runtime, {
		topicId,
		chapterId: first.id,
		chapterIndex: first.index,
		status: "in_progress",
		progressPercent: 5,
	});
	// A fast Back can arrive before this progress write resolves. In that case
	// Home has already done one silent read; calibrate it once more after the
	// authoritative write without reintroducing loading placeholders.
	if (root.querySelector('#view-home.active')) void reconcileHomeRecent(root, state.runtime.api);
  } catch (error) {
    handleApiError(state.runtime, state.effectiveAccessPlan, state.isLoggedIn, error, { root, intent: "progress" });
  }
}

function syncFavoriteCardState(root: Root, topicId: string, favorited: boolean): void {
	root.querySelectorAll<HTMLElement>('.topic-card[data-id], .recent-card[data-id]').forEach((card) => {
		if (card.dataset.id !== topicId) return;
		card.toggleAttribute('data-favorited', favorited);
		const favorite = card.querySelector<HTMLElement>('.fav-btn');
		favorite?.classList.toggle('active', favorited);
		favorite?.classList.toggle('starred', favorited);
		favorite?.setAttribute('aria-pressed', String(favorited));
	});
	root.querySelectorAll<HTMLElement>('.fav-btn[data-topic-id]').forEach((favorite) => {
		if (favorite.dataset.topicId !== topicId) return;
		favorite.classList.toggle('active', favorited);
		favorite.classList.toggle('starred', favorited);
		favorite.setAttribute('aria-pressed', String(favorited));
	});
	const miniFavorite = root.querySelector<HTMLElement>('#miniFavoriteBtn');
	if (miniFavorite?.dataset.topicId === topicId) paintMiniPlayerFavorite(miniFavorite, favorited);
}

function readTopicFavoriteState(root: Root, topicId: string, fallback: HTMLElement): boolean {
	// The event source is the most recent rendered instance. Prefer its
	// accessible state when a just-reconciled Saved card and a retained Browse
	// snapshot briefly disagree, then use a same-Topic peer as a fallback.
	const sourcePressed = fallback.getAttribute('aria-pressed');
	if (sourcePressed === 'true') return true;
	if (sourcePressed === 'false') return false;
	const favorites = Array.from(root.querySelectorAll<HTMLElement>('.fav-btn')).filter((button) => {
		if (button.dataset.topicId === topicId) return true;
		return button.closest<HTMLElement>('.topic-card[data-id], .recent-card[data-id]')?.dataset.id === topicId;
	});
	const canonical = favorites.find((button) => button.getAttribute('aria-pressed') !== null) ?? fallback;
	const pressed = canonical.getAttribute('aria-pressed');
	if (pressed === 'true') return true;
	if (pressed === 'false') return false;
	return canonical.classList.contains('active') || canonical.classList.contains('starred');
}

function readTopicFavoriteFromSource(source: HTMLElement, topicId: string): boolean | undefined {
	const card = source.closest<HTMLElement>('.topic-card[data-id], .recent-card[data-id]');
	if (card?.dataset.id !== topicId) return undefined;
	const favorite = card.querySelector<HTMLElement>('.fav-btn');
	const pressed = favorite?.getAttribute('aria-pressed');
	return pressed === 'true' ? true : pressed === 'false' ? false : undefined;
}

function markTopicFavoriteWrite(root: Root, topicId: string, source: HTMLElement): void {
	source.dataset.topicFavoriteStateVersion = String(bumpTopicFavoriteVersion(root, topicId));
}

function markTopicVoteWrite(root: Root, topicId: string, source: HTMLElement): void {
	source.dataset.topicVoteStateVersion = String(bumpTopicVoteVersion(root, topicId));
}

function syncBrowseTopicViewerState(root: Root, topicId: string, patch: { isFavorite?: boolean; voteValue?: -1 | 0 | 1 }): void {
	patchBrowseViewTopicViewerState(topicId, patch);
	patchActiveBrowseTopicViewerState(root, topicId, patch);
}

function syncTopicVoteState(root: Root, topicId: string, voteValue: number): void {
	root.querySelectorAll<HTMLElement>('[data-topic-id]').forEach((button) => {
		if (button.dataset.topicId !== topicId) return;
		if (button.classList.contains('like-btn')) {
			const active = voteValue === 1;
			button.classList.toggle('active', active);
			button.setAttribute('aria-pressed', String(active));
		}
		if (button.classList.contains('dislike-btn')) {
			const active = voteValue === -1;
			button.classList.toggle('active', active);
			button.setAttribute('aria-pressed', String(active));
		}
	});
}

function readTopicVoteState(root: Root, topicId: string): -1 | 0 | 1 {
	const buttons = Array.from(root.querySelectorAll<HTMLElement>('[data-topic-id]')).filter((button) => button.dataset.topicId === topicId);
	if (buttons.some((button) => button.classList.contains('like-btn') && button.classList.contains('active'))) return 1;
	if (buttons.some((button) => button.classList.contains('dislike-btn') && button.classList.contains('active'))) return -1;
	return 0;
}

function isTopicActionPending(requests: WeakMap<object, Set<string>>, root: Root, topicId: string): boolean {
	return requests.get(root as object)?.has(topicId) ?? false;
}

function setTopicActionPending(
	requests: WeakMap<object, Set<string>>,
	root: Root,
	topicId: string,
	pending: boolean,
	options: { keepMiniFavoriteEnabled?: boolean } = {},
): void {
	const key = root as object;
	const active = requests.get(key) ?? new Set<string>();
	const timers = topicActionPendingTimers.get(key) ?? new Map<string, ReturnType<typeof setTimeout>>();
	if (pending) active.add(topicId);
	else {
		active.delete(topicId);
		const pendingTimer = timers.get(topicId);
		if (pendingTimer) {
			clearTimeout(pendingTimer);
			timers.delete(topicId);
		}
	}
	if (active.size > 0) requests.set(key, active);
	else requests.delete(key);
	if (pending) {
		// Force-release after the cap even if the API never settles; when the
		// honest release lands first it cancels this timer. Replace any stale
		// timer so a re-click cannot be released early by the previous one.
		const previous = timers.get(topicId);
		if (previous) clearTimeout(previous);
		const pendingTimer = setTimeout(() => {
			timers.delete(topicId);
			setTopicActionPending(requests, root, topicId, false, options);
		}, TOPIC_ACTION_PENDING_CAP_MS);
		timers.set(topicId, pendingTimer);
		topicActionPendingTimers.set(key, timers);
	}
	root.querySelectorAll<HTMLButtonElement>('[data-topic-id]').forEach((button) => {
		if (button.dataset.topicId !== topicId) return;
		if (options.keepMiniFavoriteEnabled && button.id === 'miniFavoriteBtn') return;
		button.disabled = pending;
	});
}

async function resumeLearning(root: Root, state: PrototypeState) {
  try {
    const response = await state.runtime.api.getResumeCard(10);
    // Follow the most recent entry unconditionally: the watermarked status is
    // pinned at 'completed' forever, so filtering completed rows here made the
    // hero's own Continue button abandon the topic it just offered.
    const resume = response.resume ?? null;
    if (!resume) {
      browseAllTopics(root);
      return;
    }
    state.runtime.activeCourseId = resume.topicSlug;
    state.runtime.activeChapterIndex = resume.chapterIndex;
    state.runtime.chapterIdByIndex[resume.topicSlug] = {
      ...(state.runtime.chapterIdByIndex[resume.topicSlug] ?? {}),
      [resume.chapterIndex]: resume.chapterId,
    };
    state.runtime.resumeProgressByChapter = {
      ...(state.runtime.resumeProgressByChapter ?? {}),
      [`${resume.topicSlug}:${resume.chapterIndex}`]: resume.progressPercent,
    };
    showArticle(resume.chapterIndex, root, state.effectiveAccessPlan);
  } catch (error) {
    recordRuntimeError(state.runtime, error);
    browseAllTopics(root);
  }
}

function browseAllTopics(root: Root): void {
  setPendingBrowseFilter("all");
  showView("home-browse", root);
  setActiveTab("home-browse", root);
}
